// ============================================
// DASHBOARD PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Briefcase,
  FileText,
  Award,
  Eye,
  TrendingUp,
  Clock,
  AlertTriangle,
  ChevronRight,
  Ship,
  FolderOpen,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useMyStats, useRecommendedJobs, useMyApplications, useExpiringCertificates, useExpiringDocuments, useMatchingJobs, useMyProfile } from '@/hooks';
import { useAuthStore } from '@/store/auth';
import { formatNumber, formatCurrency, formatRelativeTime, applicationStatusLabels, getStatusVariant, calculateProfileCompletion } from '@/utils';

// Başvuru durumu — enums namespace yüklenene kadar kullanılan statik fallback
const APPLICATION_STATUS_TR: Record<string, string> = {
  PENDING: 'Beklemede',
  REVIEWED: 'İncelendi',
  SHORTLISTED: 'Ön Eleme',
  INTERVIEW: 'Mülakat',
  OFFERED: 'Teklif Yapıldı',
  ACCEPTED: 'Kabul Edildi',
  REJECTED: 'Reddedildi',
  WITHDRAWN: 'Geri Çekildi',
};

// Gemi türleri - getter trap bypass
const VESSEL_TYPES: Record<string, { tr: string; en: string }> = {
  BULK_CARRIER: { tr: 'Dökme Yük Gemisi', en: 'Bulk Carrier' },
  TANKER: { tr: 'Tanker', en: 'Tanker' },
  CONTAINER: { tr: 'Konteyner Gemisi', en: 'Container Ship' },
  GENERAL_CARGO: { tr: 'Genel Kargo', en: 'General Cargo' },
  RORO: { tr: 'Ro-Ro', en: 'Ro-Ro' },
  PASSENGER: { tr: 'Yolcu Gemisi', en: 'Passenger Ship' },
  OFFSHORE: { tr: 'Açık Deniz', en: 'Offshore' },
  TUG: { tr: 'Römorkör', en: 'Tug' },
  YACHT: { tr: 'Yat', en: 'Yacht' },
  FISHING: { tr: 'Balıkçı Gemisi', en: 'Fishing Vessel' },
  OTHER: { tr: 'Diğer', en: 'Other' },
};
import { MatchScoreBadge } from '@/components';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/')({
  component: DashboardPage,
});

function DashboardPage() {
  const { t, i18n } = useTranslation(['panel', 'enums']);
  const { user } = useAuthStore();
  const { data: statsResponse, isLoading: statsLoading, isError: statsError } = useMyStats();
  const { data: profileResponse } = useMyProfile();
  const { data: jobsResponse, isLoading: jobsLoading, isError: jobsError } = useRecommendedJobs({ limit: 5 });
  const { data: applicationsResponse, isLoading: appsLoading, isError: appsError } = useMyApplications();
  const { data: expiringResponse } = useExpiringCertificates(90);
  const { data: expiringDocsResponse } = useExpiringDocuments(90);
  const { data: matchingJobsResponse, isLoading: matchingLoading, isError: matchingError } = useMatchingJobs();

  // Extract data from API responses (handles { success, data } wrapper)
  const stats: any = (statsResponse as any)?.data || statsResponse || {};
  const profile: any = (profileResponse as any)?.data || profileResponse;
  const jobsData: any = (jobsResponse as any)?.data || jobsResponse || {};
  const jobs: any[] = jobsData?.data || jobsData || [];
  const applications: any[] = (applicationsResponse as any)?.data || applicationsResponse || [];
  const expiring: any = (expiringResponse as any)?.data || expiringResponse || {};
  const expiringDocs: any[] = (expiringDocsResponse as any)?.data || expiringDocsResponse || [];
  const matchingJobs: any[] = Array.isArray(matchingJobsResponse) 
    ? matchingJobsResponse 
    : ((matchingJobsResponse as any)?.data || []);

  const queryClient = useQueryClient();
  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['profile', 'stats'] });
    queryClient.invalidateQueries({ queryKey: ['jobs', 'recommended'] });
    queryClient.invalidateQueries({ queryKey: ['applications'] });
    queryClient.invalidateQueries({ queryKey: ['matching', 'jobs'] });
  };

  const firstName = user?.seafarer?.firstName || t('dashboard.seafarer');

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.welcome', { name: firstName })}</h1>
          <p className="text-gray-500 mt-1">{t('dashboard.todaySummary')}</p>
        </div>
        <Link to="/jobs" className="btn-primary">
          <Briefcase className="w-4 h-4" />
          {t('dashboard.searchJobs')}
        </Link>
      </div>

      {/* Bağlantı Hatası Banner */}
      {statsError && (
        <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Veriler yüklenemedi. Bağlantınızı kontrol edip tekrar deneyin.</span>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 hover:bg-red-200 rounded-lg text-sm font-medium transition-colors ml-4 flex-shrink-0"
          >
            Yenile
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('dashboard.totalApplications')}
          value={stats?.totalApplications || 0}
          icon={FileText}
          color="blue"
        />
        <StatCard
          title={t('dashboard.pendingApplications')}
          value={stats?.pendingApplications || 0}
          icon={Clock}
          color="amber"
        />
        <StatCard
          title={t('dashboard.profileViews')}
          value={stats?.profileViews || 0}
          icon={Eye}
          color="green"
          change={stats?.profileViewsChange}
        />
        <StatCard
          title={t('dashboard.certificateCount')}
          value={stats?.totalCertificates || 0}
          icon={Award}
          color="purple"
        />
      </div>

      {/* Son Başvurular + Profil Tamamlama — Stats'ın hemen altında */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Son Başvurular */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.recentApplications')}</h2>
            <Link to="/applications" className="text-sm text-maritime-600 hover:text-maritime-700">
              {t('dashboard.all')}
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {appsLoading ? (
              <LoadingSkeleton count={3} />
            ) : appsError ? (
              <div className="p-4 text-center text-sm text-gray-400">{t('dashboard.loadError', { defaultValue: 'Yüklenemedi' })}</div>
            ) : applications.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {t('dashboard.noApplications')}
              </div>
            ) : (
              applications.slice(0, 4).map((app: any) => (
                <div key={app.id} className="p-4">
                  <p className="font-medium text-gray-900 text-sm truncate">{app.job?.title}</p>
                  <p className="text-xs text-gray-500">{app.job?.company?.name}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className={`badge badge-${getStatusVariant(app.status) === 'default' ? 'gray' : getStatusVariant(app.status)}`}>
                      {t(`enums:applicationStatus.${app.status}`, { defaultValue: APPLICATION_STATUS_TR[app.status] || applicationStatusLabels[app.status] || app.status })}
                    </span>
                    <span className="text-xs text-gray-400">{formatRelativeTime(app.createdAt)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Profil Tamamlama */}
        <div className="card">
          <div className="card-body">
            <h3 className="font-semibold text-gray-900 mb-4">{t('dashboard.profileCompletion')}</h3>
            <div className="flex items-center justify-center mb-4">
              <div className="relative w-32 h-32">
                <svg className="w-32 h-32 -rotate-90" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                  <circle
                    cx="60" cy="60" r="50" fill="none"
                    stroke="#0284c7" strokeWidth="10"
                    strokeDasharray={`${2 * Math.PI * 50}`}
                    strokeDashoffset={`${2 * Math.PI * 50 * (1 - (profile ? calculateProfileCompletion(profile) : 0) / 100)}`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-gray-900">{profile ? calculateProfileCompletion(profile) : 0}%</span>
                </div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div
                className="bg-maritime-600 h-2 rounded-full transition-all"
                style={{ width: `${profile ? calculateProfileCompletion(profile) : 0}%` }}
              />
            </div>
            <p className="text-sm text-gray-500 text-center mb-3">{t('dashboard.completed', { percent: profile ? calculateProfileCompletion(profile) : 0 })}</p>
            {(profile ? calculateProfileCompletion(profile) : 0) < 100 && (
              <Link to="/profile" className="btn-primary w-full justify-center">
                {t('dashboard.completeProfile')}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Grid — İlanlar yan yana */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* En Uygun İlanlar */}
        {matchingJobs.length > 0 && (
          <div className="card border-green-200 bg-green-50/30">
            <div className="card-header flex items-center justify-between border-green-100">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                </span>
                <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.bestMatchingJobs')}</h2>
              </div>
              <Link to="/jobs" className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1">
                {t('dashboard.viewAll')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="divide-y divide-green-100">
              {matchingLoading ? (
                <LoadingSkeleton count={3} />
              ) : matchingError ? (
                <div className="p-4 text-center text-sm text-gray-400">{t('dashboard.loadError', { defaultValue: 'Yüklenemedi' })}</div>
              ) : (
                matchingJobs.slice(0, 4).map((match: any) => (
                  <Link
                    key={match.job?.id}
                    to="/jobs/$slug"
                    params={{ slug: match.job?.slug }}
                    className="block p-4 hover:bg-green-50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-maritime bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Ship className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-gray-900 truncate text-sm">{match.job?.title}</h3>
                          <MatchScoreBadge score={match.matchScore} size="sm" showLabel={false} />
                        </div>
                        <p className="text-xs text-gray-500">{match.job?.company?.name}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          <span>{VESSEL_TYPES[match.job?.vesselType]?.[i18n.language === 'en' ? 'en' : 'tr'] || match.job?.vesselType}</span>
                          {match.job?.minSalary && (
                            <span>{formatCurrency(match.job?.minSalary, match.job?.currency)} - {formatCurrency(match.job?.maxSalary, match.job?.currency)}</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        )}

        {/* Önerilen İlanlar */}
        <div className={matchingJobs.length > 0 ? '' : 'lg:col-span-2'}>
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.recommendedJobs')}</h2>
              <Link to="/jobs" className="text-sm text-maritime-600 hover:text-maritime-700 flex items-center gap-1">
                {t('dashboard.viewAll')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="divide-y divide-gray-100">
              {jobsLoading ? (
                <LoadingSkeleton count={3} />
              ) : jobsError ? (
                <div className="p-6 text-center text-gray-400 text-sm">{t('dashboard.loadError', { defaultValue: 'Yüklenemedi' })}</div>
              ) : jobs.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <Briefcase className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  <p>{t('dashboard.noRecommendedJobs')}</p>
                </div>
              ) : (
                jobs.slice(0, 5).map((job: any) => (
                  <Link
                    key={job.id}
                    to="/jobs/$slug"
                    params={{ slug: job.slug }}
                    className="block p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-maritime bg-maritime-100 flex items-center justify-center flex-shrink-0">
                        <Ship className="w-5 h-5 text-maritime-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate text-sm">{job.title}</h3>
                        <p className="text-xs text-gray-500">{job.company?.name}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          <span>{VESSEL_TYPES[job.vesselType]?.[i18n.language === 'en' ? 'en' : 'tr'] || job.vesselType}</span>
                          {job.minSalary && (
                            <span>{formatCurrency(job.minSalary, job.currency)} - {formatCurrency(job.maxSalary, job.currency)}</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Uyarı Kartları — Sertifika & Belge */}
      {((expiring && (expiring.expired?.length > 0 || expiring.expiringSoon?.length > 0)) || (expiringDocs && expiringDocs.length > 0)) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Süresi Dolan Sertifikalar */}
          {expiring && (expiring.expired?.length > 0 || expiring.expiringSoon?.length > 0) && (
            <div className="card border-amber-200 bg-amber-50/50">
              <div className="card-header border-amber-100">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" />
                  <h2 className="font-semibold">{t('dashboard.certificateAlerts')}</h2>
                </div>
              </div>
              <div className="p-4 space-y-3">
                {expiring.expired?.map((cert: any) => (
                  <div key={cert.id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">{cert.certificateType?.name}</span>
                    <span className="badge badge-error">{t('dashboard.expired')}</span>
                  </div>
                ))}
                {expiring.expiringSoon?.map((cert: any) => (
                  <div key={cert.id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">{cert.certificateType?.name}</span>
                    <span className="badge badge-warning">{t('dashboard.expiringSoon')}</span>
                  </div>
                ))}
                <Link to="/profile/certificates" className="text-sm text-maritime-600 hover:text-maritime-700 block mt-2">
                  {t('dashboard.manageCertificates')}
                </Link>
              </div>
            </div>
          )}

          {/* Süresi Dolan Belgeler */}
          {expiringDocs && expiringDocs.length > 0 && (
            <div className="card border-orange-200 bg-orange-50/50">
              <div className="card-header border-orange-100">
                <div className="flex items-center gap-2 text-orange-700">
                  <FolderOpen className="w-5 h-5" />
                  <h2 className="font-semibold">{t('dashboard.documentAlerts')}</h2>
                </div>
              </div>
              <div className="p-4 space-y-3">
                {expiringDocs.slice(0, 5).map((doc: any) => {
                  const isExpired = new Date(doc.expiryDate) < new Date();
                  return (
                    <div key={doc.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 truncate flex-1">{doc.name}</span>
                      <span className={`badge ${isExpired ? 'badge-error' : 'badge-warning'}`}>
                        {isExpired ? t('dashboard.expired') : t('dashboard.expiringSoon')}
                      </span>
                    </div>
                  );
                })}
                <Link to="/profile/documents" className="text-sm text-maritime-600 hover:text-maritime-700 block mt-2">
                  {t('dashboard.manageDocuments')}
                </Link>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ title, value, icon: Icon, color, change, loading }: {
  title: string;
  value: number;
  icon: any;
  color: 'blue' | 'amber' | 'green' | 'purple';
  change?: number;
}) {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className="card p-4">
      <div className={`w-10 h-10 rounded-maritime ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-sm text-gray-500">{title}</p>
      <div className="flex items-end gap-2">
        <p className="text-2xl font-bold text-gray-900">{formatNumber(value)}</p>
        {change !== undefined && change !== 0 && (
          <span className={`text-xs flex items-center ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
            <TrendingUp className={`w-3 h-3 ${change < 0 ? 'rotate-180' : ''}`} />
            {Math.abs(change)}%
          </span>
        )}
      </div>
    </div>
  );
}

// Loading Skeleton
function LoadingSkeleton({ count = 3 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="p-4">
          <div className="flex items-start gap-4">
            <div className="skeleton w-12 h-12 rounded-maritime" />
            <div className="flex-1">
              <div className="skeleton h-4 w-3/4 mb-2" />
              <div className="skeleton h-3 w-1/2" />
            </div>
          </div>
        </div>
      ))}
    </>
  );
}
